/**
 * mixed-cpu の Web Worker プロキシ。
 *
 * 旧版は main thread 上で同期 WASM 呼び出ししていたが、 Renju/Backgammon/Jungo Hard が
 * 2 秒近く main thread を block して mobile UX を損なっていた (docs/CPU_PERF.md)。
 * 本実装は CPU 計算を Web Worker に逃がして UI スレッドを開放する。
 *
 * 同時に main bundle から pkg-cpu (~355KB wasm + JS glue) を切り離す効果もある (= 別 chunk)。
 *
 * 入力 / 出力の state / mv は mixed-engine の シリアライズと同じ JSON 形 (snake_case 等)。
 *
 * `bgRollDice` は WASM を使わない TS 単独実装に置換 (= getrandom 不要、 sync で済む)。
 */

import CpuWorker from './cpu-worker?worker';
import type { GameId, GameMove, GameState } from './index';

export type CpuLevel = 'easy' | 'normal' | 'hard';
export type SideName = 'First' | 'Second';

type WorkerMsg =
  | { type: 'ready' }
  | { type: 'init-error'; message: string }
  | { type: 'pick-result'; id: number; mv: GameMove | null }
  | { type: 'pick-error'; id: number; message: string };

let worker: Worker | null = null;
let readyPromise: Promise<void> | null = null;
let nextId = 1;
const pending = new Map<number, { resolve: (mv: GameMove | null) => void; reject: (e: Error) => void }>();

export function ensureCpuWasm(): Promise<void> {
  if (readyPromise) return readyPromise;
  worker = new CpuWorker();
  readyPromise = new Promise<void>((resolve, reject) => {
    let initSettled = false;
    worker!.addEventListener('message', (e: MessageEvent<WorkerMsg>) => {
      const m = e.data;
      switch (m.type) {
        case 'ready':
          if (!initSettled) {
            initSettled = true;
            resolve();
          }
          break;
        case 'init-error':
          if (!initSettled) {
            initSettled = true;
            reject(new Error(m.message));
          }
          break;
        case 'pick-result': {
          const p = pending.get(m.id);
          if (p) {
            pending.delete(m.id);
            p.resolve(m.mv);
          }
          break;
        }
        case 'pick-error': {
          const p = pending.get(m.id);
          if (p) {
            pending.delete(m.id);
            p.reject(new Error(m.message));
          }
          break;
        }
      }
    });
    worker!.addEventListener('error', (e) => {
      if (!initSettled) {
        initSettled = true;
        reject(new Error(`worker error: ${e.message ?? '(no message)'}`));
      }
      // pick 中の error: 全 pending を reject
      const err = new Error(`worker error: ${e.message ?? '(no message)'}`);
      for (const [id, p] of pending) {
        p.reject(err);
        pending.delete(id);
      }
    });
    worker!.postMessage({ type: 'init' });
  });
  return readyPromise;
}

/**
 * 指定 game / state / 手番 / 強さで CPU が選んだ 1 手を返す。
 * 合法手が無い場合は `null`。 内部で `ensureCpuWasm` を await する。
 */
export async function pickMove(
  game: GameId,
  state: GameState,
  side: SideName,
  level: CpuLevel,
): Promise<GameMove | null> {
  await ensureCpuWasm();
  const id = nextId++;
  return new Promise<GameMove | null>((resolve, reject) => {
    pending.set(id, { resolve, reject });
    worker!.postMessage({ type: 'pick', id, game, state, side, level });
  });
}

/**
 * Backgammon の `state.dice` が空ならローカル擬似乱数で振る。 dice 入りの新 state を返す。
 *
 * 旧版は mixed-cpu WASM の `bg_roll_dice` を呼んでいたが、 ロジックが単純 (d6 を 2 回 +
 * 初手ゾロ目禁止) なので main thread の TS で実装し直して WASM 依存を切る。
 * これで dice ロールは worker 経由の往復不要 = 同期で済む。
 */
export function bgRollDice(state: unknown): unknown {
  const s = state as { dice?: number[]; round?: number };
  const dice = s.dice ?? [];
  if (dice.length > 0) return state;
  const initialRoll = (s.round ?? 0) === 0;
  const buf = new Uint8Array(2);
  for (;;) {
    crypto.getRandomValues(buf);
    const d1 = (buf[0] % 6) + 1;
    const d2 = (buf[1] % 6) + 1;
    if (initialRoll && d1 === d2) continue; // 初手ゾロ目禁止
    return { ...s, dice: d1 === d2 ? [d1, d1, d1, d1] : [d1, d2] };
  }
}
